// 分层定价服务层（对接后端《商品管理分层定价后端技术方案》）
// 接口①：复用 /product/price/getDefaultNativePrices，传入 tierPrices + countryTierMappings 批量生成本地价
// 接口②：/product/template/get 查询国家-层级模板
// 保存：bizInfo.tieredPricing 并入 /product/addProduct、/product/modProduct（无独立保存接口）
import { queryPriceList, getConfTemplate } from '@/api/goodsSetting'

// 后端契约常量
export const SCHEMA_VERSION = 1
export const PRICING_MODE_MULTI = 'MULTI_DEFAULT_PRICE'
export const TEMPLATE_TYPE_PRICE_TIER = 'tieredPricing'

// 层级基准价 / 国家层级映射（直接复用后端字段定义）
export type TierPrice = goodsSetting.tierPriceItem
export type CountryTierMapping = goodsSetting.countryTierMappingItem

// 本地价格表的单行（国家最终本地价 + 所属层级）
export interface TierCountryRow {
  regionCode: string
  regionName: string
  prodPriceCurrency: string
  prodPrice: string // 当前本地价（接口①生成，可单行手改）
  originProdPrice: string // 系统生成原价（用于标记手改/还原）
  tierCode: string // 所属价格层级
}

// 接口①：按层级基准价 + 国家映射批量生成各国最终本地价
// 分层无单一默认价：顶层 prodPrice/prodPriceCurrency/priceId/priceTier 一律按 T1 层级铺设，与单一流入参口径一致
export const generateTieredNativePrices = async (params: {
  dbtId: string
  pkg: string
  prodChnl: string
  prodType: string
  tierPrices: TierPrice[]
  countryTierMappings: CountryTierMapping[]
  applySgtAdjustment?: boolean
}): Promise<goodsSetting.goodsPriceItem[]> => {
  const t1 = params.tierPrices.find((t) => t.tierCode === 'T1') ?? params.tierPrices[0]
  const res = await queryPriceList({
    dbtId: params.dbtId,
    pkg: params.pkg,
    prodChnl: params.prodChnl,
    prodType: params.prodType,
    // 顶层默认价取 T1，与单一价格流入参口径一致
    prodPrice: t1?.prodPrice,
    prodPriceCurrency: t1?.prodPriceCurrency,
    // 苹果档位标识仅在有值时下发；谷歌无档位（priceId/priceTier 为空），
    // 不下发以对齐单一价格流，避免后端解析空档位标识报 500
    ...(t1?.priceId ? { priceId: t1.priceId } : {}),
    ...(t1?.priceTier ? { priceTier: t1.priceTier } : {}),
    applySgtAdjustment: params.applySgtAdjustment,
    tierPrices: params.tierPrices,
    countryTierMappings: params.countryTierMappings,
  })
  return res?.code === '0' ? res.data?.productNativePrice ?? [] : []
}

// 接口②：查询国家-层级模板，返回国家层级映射（无模板返回 null）
// includeAllProdTypes=true 时跨类型查询，优先取当前 prodType 命中的模板，否则取第一条
export const fetchTierTemplateMappings = async (params: {
  dbtId: string
  prodChnl: string
  prodType: string
}): Promise<CountryTierMapping[] | null> => {
  const res = await getConfTemplate({
    dbtId: params.dbtId,
    prodChnl: params.prodChnl,
    prodType: params.prodType,
    templateType: TEMPLATE_TYPE_PRICE_TIER,
    includeAllProdTypes: true,
  })
  if (res?.code !== '0') return null
  // 后端返回单个模板，countryTierMappings 直接挂在 data 上
  const mappings: CountryTierMapping[] = res.data?.countryTierMappings ?? []
  return mappings.length ? mappings : null
}

// 组装保存用 bizInfo.tieredPricing
export const buildTieredPricing = (data: {
  tierPrices: TierPrice[]
  countryTierMappings: CountryTierMapping[]
  saveAsTemplate: boolean
}): goodsSetting.tieredPricingInfo => ({
  schemaVersion: SCHEMA_VERSION,
  pricingMode: PRICING_MODE_MULTI,
  tierPrices: data.tierPrices,
  countryTierMappings: data.countryTierMappings,
  saveAsTemplate: data.saveAsTemplate,
})

// 保存用 tierPrices 对齐 countryTierMappings：只下发「至少有一个国家归属」的层级（T1 兜底层级始终下发）。
// 与接口① generate 的 effectiveTierPrices 同口径：现存本地价里没有任何国家归属的层级（如设了 T3 价但无 T3 国家）不下发。
export const filterUsedTierPrices = (tierPrices: TierPrice[], mappings: CountryTierMapping[]): TierPrice[] => {
  const usedTiers = new Set(mappings.map((m) => m.tierCode))
  usedTiers.add('T1')
  return tierPrices.filter((t) => usedTiers.has(t.tierCode))
}

// 保存校验：空置层级 = 配了价格但无任何国家映射，且非 T1（T1 永久兜底豁免）
export const getUnusedTiers = (validTierCodes: string[], mappings: CountryTierMapping[]): string[] => {
  const assigned = new Set(mappings.map((m) => m.tierCode))
  return validTierCodes.filter((code) => code !== 'T1' && !assigned.has(code))
}

export interface RecognizeItem {
  input: string // 用户原始输入
  regionCode: string
  regionName: string
}
export interface RecognizeResult {
  recognized: RecognizeItem[]
  unrecognized: string[] // 无法识别的原始输入
}

// 快捷录入国家识别（后端不提供识别接口，按本地价格表已加载的国家全集匹配）
export const recognizeCountries = async (
  inputs: string[],
  universe: Pick<TierCountryRow, 'regionCode' | 'regionName'>[],
): Promise<RecognizeResult> => {
  const recognized: RecognizeItem[] = []
  const unrecognized: string[] = []
  inputs.forEach((raw) => {
    const key = raw.trim()
    if (!key) return
    const lower = key.toLowerCase()
    const matched = universe.find((c) => c.regionCode.toLowerCase() === lower || c.regionName.toLowerCase() === lower)
    if (matched) {
      recognized.push({ input: key, regionCode: matched.regionCode, regionName: matched.regionName })
    } else {
      unrecognized.push(key)
    }
  })
  return { recognized, unrecognized }
}
